// End-to-end encrypted names for spaces and channels (and space symbols). Each space has a random key that
// only members' devices hold: newcomers get it from the invite link's #fragment (never
// sent to any server), your own devices get it through your private self-group.
// Libraries work the same way with a library key, which is stored wrapped under the key of
// each space the library is shared with (docs/STORAGE.md §3). Same format as hrth-core's
// meta.rs.
import { b64url, fromB64url } from './attachments'

const PREFIX = 'enc:v1:'

/** What a ciphertext is (bound in as associated data, so one can't stand in for another). */
export type MetaKind =
  | 'space'
  | 'channel'
  | 'icon'
  | 'color'
  | `library:${string}`
  | `library-key:${string}`
  | `library-entry:${string}`
  | `library-settings:${string}`
  | `repo-ref:${string}`
  | `repo-pull:${string}`
  | `storage-location:${string}`
  | `library-storage:${string}`
  | `library-storage-write:${string}`
  | `node-library-key:${string}`
  | `node-rules:${string}`

/** A library's name, under the library key. */
export const libraryName = (library: string): MetaKind => `library:${library}`
/** A library's key, wrapped under a space key. */
export const libraryKeyWrap = (library: string): MetaKind => `library-key:${library}`

/** A random 32-byte key, base64url (space and library keys alike). */
export function generateSpaceKey(): string {
  return b64url(crypto.getRandomValues(new Uint8Array(new ArrayBuffer(32))))
}

async function key(k: string, usage: KeyUsage): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', fromB64url(k), 'AES-GCM', false, [usage])
}

function aad(kind: MetaKind): Uint8Array<ArrayBuffer> {
  const a = new TextEncoder().encode(`hearth-meta:v1:${kind}`)
  const out = new Uint8Array(new ArrayBuffer(a.length))
  out.set(a)
  return out
}

export function isEncrypted(name: string | null | undefined): boolean {
  return !!name && name.startsWith(PREFIX)
}

export async function encryptName(spaceKey: string, kind: MetaKind, name: string): Promise<string> {
  const nonce = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(12)))
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: nonce, additionalData: aad(kind) },
      await key(spaceKey, 'encrypt'),
      new TextEncoder().encode(name),
    ),
  )
  const out = new Uint8Array(12 + ct.length)
  out.set(nonce)
  out.set(ct, 12)
  return PREFIX + b64url(out)
}

/** Decrypts an encrypted name; plain names pass through; null if the key is wrong. */
export async function decryptName(
  spaceKey: string | undefined,
  kind: MetaKind,
  name: string,
): Promise<string | null> {
  if (!isEncrypted(name)) return name
  if (!spaceKey) return null
  try {
    const raw = fromB64url(name.slice(PREFIX.length))
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: raw.slice(0, 12), additionalData: aad(kind) },
      await key(spaceKey, 'decrypt'),
      raw.slice(12),
    )
    return new TextDecoder().decode(pt)
  } catch {
    return null
  }
}

/** The space key carried in an invite link's fragment (`#k=...`), if any. */
export function keyFromInvite(url: string): string | null {
  const hash = url.split('#')[1] ?? ''
  const k = new URLSearchParams(hash).get('k')
  return k && /^[A-Za-z0-9_-]{43}$/.test(k) ? k : null
}

export function withKey(inviteUrl: string, spaceKey: string | undefined): string {
  return spaceKey ? `${inviteUrl.split('#')[0]}#k=${spaceKey}` : inviteUrl
}

export const MAX_DEVICE_NAME = 64

/** A device name as typed, trimmed; null if blank, over 64 characters, or with control characters (as hrth-proto). */
export function cleanDeviceName(raw: string): string | null {
  const name = raw.trim()
  return name && [...name].length <= MAX_DEVICE_NAME && !/[\u0000-\u001f\u007f-\u009f]/.test(name) ? name : null
}

/** A history transfer offered with a device link (HRTH-202): its key, and the device sending. */
export interface LinkTransfer {
  /** 32 random bytes, base64url. */
  key: string
  from: string
}

/**
 * A device link carrying the new device's name in its fragment (`#n=`, base64url UTF-8),
 * which browsers never send; with `transfer`, also its key (`t=`) and sender (`f=`).
 */
export function linkWithName(url: string, name: string, transfer?: LinkTransfer): string {
  const t = transfer ? `&t=${transfer.key}&f=${encodeURIComponent(transfer.from)}` : ''
  return `${url.split('#')[0]}#n=${b64url(new TextEncoder().encode(name))}${t}`
}

/** The history transfer a link offers, if it carries a well-formed one. */
export function transferFromLink(url: string): LinkTransfer | null {
  const p = new URLSearchParams(url.split('#')[1] ?? '')
  const key = p.get('t')
  const from = p.get('f')
  if (!key || !from || !/^[A-Za-z0-9_-]{43}$/.test(key)) return null
  return { key, from }
}

/** The device name in a link's fragment, if there's a valid one. */
export function nameFromLink(url: string): string | null {
  const raw = new URLSearchParams(url.split('#')[1] ?? '').get('n')
  if (!raw) return null
  try {
    return cleanDeviceName(new TextDecoder('utf-8', { fatal: true }).decode(fromB64url(raw)))
  } catch {
    return null
  }
}

/** Wraps a library key under a space key, for storing with the share. */
export function wrapLibraryKey(spaceKey: string, library: string, libraryKey: string): Promise<string> {
  return encryptName(spaceKey, libraryKeyWrap(library), libraryKey)
}

/** The library key from a wrap, or null if it isn't one this space key opens. */
export async function unwrapLibraryKey(spaceKey: string, library: string, wrapped: string): Promise<string | null> {
  if (!isEncrypted(wrapped)) return null
  const k = await decryptName(spaceKey, libraryKeyWrap(library), wrapped)
  return k && /^[A-Za-z0-9_-]{43}$/.test(k) ? k : null
}

/** A library one of my nodes made: its key, sealed under that node location's key. */
export async function unwrapNodeLibraryKey(locationKey: string, library: string, wrapped: string): Promise<string | null> {
  if (!isEncrypted(wrapped)) return null
  const k = await decryptName(locationKey, `node-library-key:${library}`, wrapped)
  return k && /^[A-Za-z0-9_-]{43}$/.test(k) ? k : null
}

/**
 * Whether `key` is the key a library's name was encrypted with. A key that arrives in a
 * message is accepted only if it opens the name, so nobody can plant a wrong one.
 */
export async function opensLibraryName(key: string, library: string, encryptedName: string): Promise<boolean> {
  return isEncrypted(encryptedName) && (await decryptName(key, libraryName(library), encryptedName)) !== null
}

/**
 * A git ref's id as the server sees it: HMAC-SHA256 of its name under the repository's
 * library key, truncated to 18 bytes. Same as hrth-core's `blind_ref`.
 */
export async function blindRef(libraryKey: string, library: string, name: string): Promise<string> {
  const k = await crypto.subtle.importKey('raw', fromB64url(libraryKey), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  const mac = await crypto.subtle.sign('HMAC', k, new TextEncoder().encode(`hrth-ref:v1:${library}:${name}`))
  return b64url(new Uint8Array(mac).slice(0, 18))
}
